import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";

export default function OwnerAccess() {
  const nav = useNavigate();
  const { setupRequired, login, setupOwner, loading, brand } = useAuth();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <p className="text-muted-jewel caps">Loading…</p>
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      if (setupRequired) {
        if (pin.length < 4) { setErr("PIN must be at least 4 digits."); setBusy(false); return; }
        if (pin !== confirm) { setErr("PINs don't match."); setBusy(false); return; }
        await setupOwner(pin);
      } else {
        await login(pin);
      }
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
      setPin("");
      setConfirm("");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-[220px] text-center text-3xl tracking-[0.4em] py-3.5 bg-panel text-ivory border border-line rounded-xl focus:outline-none";

  const title = brand?.title || "Register";
  const subtitle = brand?.subtitle || "";

  return (
    <div className="min-h-screen bg-app text-ivory font-body">
      <div className="max-w-[560px] mx-auto px-5">
        <div className="flex items-center justify-between pt-6 pb-2">
          <div>
            <div className="font-serif text-[26px] font-semibold text-ivory tracking-wide leading-tight">
              {title}
            </div>
            {subtitle && <div className="caps-lg text-gold mt-1">{subtitle}</div>}
          </div>
        </div>

        <div className="pt-16 text-center">
          <div className="font-serif text-3xl font-semibold" data-testid="owner-title">
            {setupRequired ? "Set owner PIN" : "Owner access"}
          </div>
          <p className="text-muted-jewel text-sm mt-2 mb-7">
            {setupRequired
              ? "First-time setup — choose a private PIN (4+ digits)."
              : "Enter the owner PIN."}
          </p>

          <input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, "")); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && !setupRequired && submit()}
            data-testid="owner-pin"
            maxLength={12}
            placeholder={setupRequired ? "New PIN" : "PIN"}
            className={inputCls + (err ? " border-jewel-red" : "")}
            autoFocus
          />
          {setupRequired && (
            <div className="mt-3">
              <input
                type="password"
                inputMode="numeric"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value.replace(/\D/g, "")); setErr(""); }}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                data-testid="owner-pin-confirm"
                maxLength={12}
                placeholder="Confirm PIN"
                className={inputCls + (err ? " border-jewel-red" : "")}
              />
            </div>
          )}
          {err && <div className="text-jewel-red text-[13px] mt-3" data-testid="owner-error">{err}</div>}

          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={() => nav("/")}
              data-testid="owner-back-btn"
              className="rounded-full border border-line text-muted-jewel px-6 py-2.5 text-sm hover:text-ivory transition-colors"
            >
              Back
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="owner-submit"
              className="seal-primary rounded-full px-8 py-2.5 text-sm font-semibold disabled:opacity-40"
            >
              {setupRequired ? "Create" : "Unlock"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
